import { useFormik } from 'formik'
import { FC, useMemo } from 'react'
import { LotacaoDtoCreate, LotacaoDtoUpdate, LotacaoView } from '../../core/_lotacaoModels'
import { MSG_SEM_PERMISSAO_LOTACAO, usePermissaoLotacaoEscrita } from '../../core/_lotacaoPermissao'
import { createLotacao, updateLotacao } from '../../core/_requests'
import { lotacaoSchema } from './validation/lotacaoSchema'
import { useT } from '_metronic/i18n/Metronici18n'
import { ButtonType, OptionsHelper, showAlert, showError } from '_metronic/helpers'
import {
  Button,
  FormInput,
  FormSelect,
  InfoAlert,
  IOption,
  ListLoading,
  LoadingIndicator,
} from 'app/components'

type Props = {
  funcionarioId: number
  empresaId?: number
  lotacao?: LotacaoView
  onSaved: () => void
  onCancel: () => void
}

export const LotacaoForm: FC<Props> = ({
  funcionarioId,
  empresaId,
  lotacao,
  onSaved,
  onCancel,
}) => {
  const { t } = useT()
  const isEditing = !!lotacao

  // Espelha os grupos autorizados para Lotacao:create/update em permission.json (backend).
  // O formulário só é renderizado por LotacoesModal quando há permissão; esta checagem é a rede
  // de segurança para qualquer outro ponto de entrada.
  const podeEditar = usePermissaoLotacaoEscrita()

  const initialValues = {
    unidade: lotacao?.unidade
      ? { label: lotacao.unidade.nome || '', value: lotacao.unidade.id }
      : (undefined as IOption<number> | undefined),
    cbo: lotacao?.cbo
      ? {
          label: `${lotacao.cbo.codigo} - ${lotacao.cbo.descricao || lotacao.cbo.nome || ''}`,
          value: lotacao.cbo.codigo,
        }
      : (undefined as IOption<string> | undefined),
    ine: lotacao?.ine || '',
  }

  const fetchOptionsParams = useMemo(() => ({ empresaId }), [empresaId])

  const formik = useFormik({
    initialValues,
    validationSchema: useMemo(() => lotacaoSchema(t, isEditing), [t, isEditing]),
    onSubmit: async (values, { setSubmitting }) => {
      if (!podeEditar) {
        showAlert({ title: 'Sem permissão', text: MSG_SEM_PERMISSAO_LOTACAO, icon: 'warning' })
        return
      }

      setSubmitting(true)
      try {
        if (isEditing) {
          // Não envia `ativo`: o backend preserva o estado atual quando o campo é omitido.
          // Enviar `true` aqui reativaria silenciosamente uma lotação inativa ao editá-la.
          const data: LotacaoDtoUpdate = {
            cbo: values.cbo?.value,
            ine: values.ine ? values.ine.trim() : undefined,
          }
          await updateLotacao(lotacao!.id, data)
        } else {
          const data: LotacaoDtoCreate = {
            unidade: values.unidade?.value as number,
            cbo: values.cbo?.value,
            ine: values.ine ? values.ine.trim() : undefined,
            ativo: true,
          }
          await createLotacao(funcionarioId, data)
        }
        showAlert({
          title: 'Sucesso!',
          text: isEditing ? 'Lotação atualizada com sucesso!' : 'Lotação criada com sucesso!',
          icon: 'success',
        })
        onSaved()
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const camposDesabilitados = formik.isSubmitting || !podeEditar

  return (
    <>
      <h5 className='fw-bold mb-3'>{isEditing ? 'Editar Lotação' : 'Cadastrar Nova Lotação'}</h5>
      {!podeEditar && (
        <InfoAlert variant='info' title='Somente leitura'>
          {MSG_SEM_PERMISSAO_LOTACAO}
        </InfoAlert>
      )}
      <form onSubmit={formik.handleSubmit} noValidate>
        <div className='row mb-3'>
          <div className='col-md-4'>
            <FormSelect
              formik={formik}
              name='unidade'
              entity='unidade_setor'
              fetchOptions={OptionsHelper.getOptionsUnidades}
              fetchOptionsParams={fetchOptionsParams}
              isClearable={false}
              isRequired
              isDisabled={isEditing || camposDesabilitados}
            />
          </div>
          <div className='col-md-4'>
            <FormSelect
              formik={formik}
              name='cbo'
              label='CBO (Ocupação)'
              placeholder='Selecione o CBO'
              fetchOptions={OptionsHelper.getOptionsCbos}
              isClearable
              isDisabled={camposDesabilitados}
            />
          </div>
          <div className='col-md-4'>
            <FormInput
              formik={formik}
              name='ine'
              label='INE (10 dígitos)'
              placeholder='Digite o código INE (opcional)'
              isDisabled={camposDesabilitados}
            />
          </div>
        </div>
        <div className='d-flex justify-content-end gap-2 mt-4'>
          <Button
            type={ButtonType.Reset}
            onClick={onCancel}
            className='btn-secondary'
            size='btn-sm'
            disabled={formik.isSubmitting}
          >
            {t('common.cancel')}
          </Button>
          {podeEditar && (
            <Button
              type={ButtonType.Submit}
              className='btn-primary'
              size='btn-sm'
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting ? <LoadingIndicator /> : 'Salvar Lotação'}
            </Button>
          )}
        </div>
        {formik.isSubmitting && <ListLoading />}
      </form>
    </>
  )
}
